/** Sustained linear beam between two points — tethers, lasers, drain beams. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { ensureBeamTexture, CRISP_SCALE } from '../../ui/CrispUtil';
import { ParticleBurst, ParticleBurstConfig } from './ParticleBurst';

export interface ChannelBeamConfig extends VfxPrimitiveConfig {
  beamWidth: number;
  beamLength: number;
  pulseAlphaRange: number;
  pulsePeriodMs: number;
  forkCount: number;
  forkSpreadAngle: number;
  formDurationMs: number;
  breakDurationMs: number;
  breakSparks: boolean;
  breakBurstConfig?: ParticleBurstConfig;
}

type BeamState = 'inactive' | 'forming' | 'sustained' | 'breaking';

export class ChannelBeam extends VfxPrimitive<ChannelBeamConfig> {
  private beams!: Phaser.GameObjects.Image[];
  private textureKey!: string;

  private state: BeamState = 'inactive';
  private targetX = 0;
  private targetY = 0;

  private pulseTweens: Phaser.Tweens.Tween[] = [];
  private transitionTweens: Phaser.Tweens.Tween[] = [];

  private breakBurst: ParticleBurst | null = null;

  protected allocatePool(): void {
    this.beams = [];
    const crispW = this.config.beamLength * CRISP_SCALE;
    const crispH = this.config.beamWidth * CRISP_SCALE;
    this.textureKey = ensureBeamTexture(this.scene, crispW, crispH);

    for (let i = 0; i < this.config.forkCount; i++) {
      const img = this.createPoolImage(this.textureKey);
      img.setOrigin(0, 0.5);
      this.beams.push(img);
    }

    if (this.config.breakSparks && this.config.breakBurstConfig) {
      this.breakBurst = new ParticleBurst(this.scene, this.config.breakBurstConfig);
    }
  }

  play(x: number, y: number, depth: number): void {
    if (this.state === 'sustained' || this.state === 'forming') return;
    this.killAllTweens();
    this.state = 'forming';

    const d = depth + (this.config.depthOffset ?? 0);
    for (const beam of this.beams) {
      beam.setPosition(x, y)
        .setDepth(d)
        .setVisible(true)
        .setActive(true)
        .setAlpha(0)
        .setScale(1 / CRISP_SCALE, 0);
    }

    this.updateBeamGeometry(x, y, this.targetX, this.targetY, d);

    const obj = { alpha: 0, scaleY: 0 };
    const tween = this.scene.tweens.add({
      targets: obj,
      alpha: 1,
      scaleY: 1 / CRISP_SCALE,
      duration: this.config.formDurationMs,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        for (const beam of this.beams) {
          beam.setAlpha(obj.alpha).setScale(beam.scaleX, obj.scaleY);
        }
      },
      onComplete: () => {
        this.state = 'sustained';
        this.startPulse();
      },
    });
    this.transitionTweens.push(tween);
  }

  setTarget(tx: number, ty: number): void {
    this.targetX = tx;
    this.targetY = ty;
  }

  stop(): void {
    if (this.state === 'inactive' || this.state === 'breaking') return;
    this.killAllTweens();
    this.state = 'breaking';

    const obj = { alpha: 1, scaleY: 1 / CRISP_SCALE };
    const tween = this.scene.tweens.add({
      targets: obj,
      alpha: 0,
      scaleY: 0,
      duration: this.config.breakDurationMs,
      ease: 'Quad.easeIn',
      onUpdate: () => {
        for (const beam of this.beams) {
          beam.setAlpha(obj.alpha).setScale(beam.scaleX, obj.scaleY);
        }
      },
      onComplete: () => {
        if (this.config.breakSparks && this.breakBurst && this.beams.length > 0) {
          const mx = (this.beams[0].x + this.targetX) / 2;
          const my = (this.beams[0].y + this.targetY) / 2;
          this.breakBurst.play(mx, my, this.beams[0].depth);
        }
        for (const beam of this.beams) this.deactivate(beam);
        this.state = 'inactive';
      },
    });
    this.transitionTweens.push(tween);
  }

  /** Reposition source end to follow the player; target uses stored targetX/Y. */
  updatePosition(x: number, y: number, depth: number, _delta: number): void {
    if (this.state === 'inactive') return;
    const d = depth + (this.config.depthOffset ?? 0);
    this.updateBeamGeometry(x, y, this.targetX, this.targetY, d);
  }

  destroy(): void {
    this.killAllTweens();
    this.breakBurst?.destroy();
    this.breakBurst = null;
    super.destroy();
  }

  private updateBeamGeometry(
    sx: number, sy: number,
    tx: number, ty: number,
    depth: number,
  ): void {
    const dx = tx - sx;
    const dy = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);
    const stretchX = dist / this.config.beamLength;

    const count = this.beams.length;
    for (let i = 0; i < count; i++) {
      const beam = this.beams[i];
      if (!beam.active && !beam.visible) continue;

      let forkAngle = angle;
      if (count > 1) {
        const offset = (i - (count - 1) / 2) * this.config.forkSpreadAngle;
        forkAngle = angle + offset;
      }

      beam.setPosition(sx, sy)
        .setRotation(forkAngle)
        .setScale(stretchX, beam.scaleY)
        .setDepth(depth);
    }
  }

  private startPulse(): void {
    this.killPulseTweens();
    const baseAlpha = 1;
    const range = this.config.pulseAlphaRange;

    for (let i = 0; i < this.beams.length; i++) {
      const tween = this.scene.tweens.add({
        targets: this.beams[i],
        alpha: { from: baseAlpha - range, to: baseAlpha + range },
        duration: this.config.pulsePeriodMs / 2,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: (i * this.config.pulsePeriodMs) / this.beams.length,
      });
      this.pulseTweens.push(tween);
    }
  }

  private killPulseTweens(): void {
    for (const t of this.pulseTweens) t.destroy();
    this.pulseTweens.length = 0;
  }

  private killAllTweens(): void {
    this.killPulseTweens();
    for (const t of this.transitionTweens) t.destroy();
    this.transitionTweens.length = 0;
  }
}
